import { useCallback, useRef, useState } from 'react';
import { type FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import {
  ACCEPT_FILE_TYPES,
  MAX_FILE_SIZE,
} from '@/modules/vip/Steps/components/VipTransfer/components/TransferRequestCard/components/VipTransferFormCard/constants';
import { useGetCommonStore } from '@/store';
import {
  getFileErrorCode,
  getFileErrorMessageKey,
  getRejectionErrorCode,
  type IVipFileError,
} from '../helpers';

interface IUseVipFileUploaderProps {
  file: File | null;
  onChange: (file: File | null) => void;
  externalError?: string;
  onFileError?: (error: IVipFileError | null) => void;
}

export const useVipFileUploader = ({
  file,
  onChange,
  externalError,
  onFileError,
}: IUseVipFileUploaderProps) => {
  const { t } = useTranslation('vip');
  const useCommonStore = useGetCommonStore();
  const isMobile = useCommonStore.use.isMobileDevice();

  const hiddenFileInput = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState('');

  const displayError = error || externalError || '';

  const selectFile = useCallback(
    (candidate?: File | null) => {
      if (!candidate) return;

      const errorCode = getFileErrorCode(candidate);
      if (errorCode) {
        setError(t(getFileErrorMessageKey(errorCode)));
        onFileError?.({ code: errorCode, file: candidate });
        return;
      }

      setError('');
      onFileError?.(null);
      onChange(candidate);
    },
    [onChange, onFileError, t]
  );

  const onDrop = useCallback(
    (accepted: File[]) => selectFile(accepted[0]),
    [selectFile]
  );

  const onDropRejected = useCallback(
    (rejections: FileRejection[]) => {
      const errorCode = getRejectionErrorCode(rejections[0]);
      setError(t(getFileErrorMessageKey(errorCode)));
      onFileError?.({ code: errorCode, file: rejections[0].file });
    },
    [onFileError, t]
  );

  const { open, getRootProps, getInputProps } = useDropzone({
    accept: ACCEPT_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    maxFiles: 1,
    disabled: !!file,
    onDrop,
    onDropRejected,
  });

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      selectFile(event.target.files?.[0]);
    },
    [selectFile]
  );

  const handleOpen = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      if (file) return;
      if (isMobile) {
        hiddenFileInput.current?.click();
      } else {
        open();
      }
    },
    [file, isMobile, open]
  );

  const handleRemove = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setError('');
      onFileError?.(null);
      onChange(null);
    },
    [onChange, onFileError]
  );

  return {
    isMobile,
    hiddenFileInput,
    displayError,
    getRootProps,
    getInputProps,
    handleFileInputChange,
    handleOpen,
    handleRemove,
  };
};
